import { useEffect, useRef, useState } from "react";
import submit from "submit-form";
import { usePrevious } from "../../../hooks/usePrevious";
import { CommentInput } from "./comment-input";
import { CommentsList } from "./comments-list";

/**
 * @param {object} props
 * @param {object} props.commentActions - { add: { url, buildBody }, delete, edit } from comment-action-configs
 * @param {object} props.commentParams - Params for buildBody (threadId, exerciseId, etc. depending on context)
 * @param {string} [props.photoId] - Optional. When used with mealComments, the current photo _id to show comments for.
 * @param {boolean} [props.isMealPhotos] - Optional. When true, the comments section is for meal photos.
 * @param {boolean} [props.commentingDisabled] - Optional. When true, the comments section is disabled. This is for legacy videos that don't have the required props for sending a comment
 */
export const CommentsSection = ({
  bodyRef,
  locals,
  commentActions,
  commentParams = {},
  photoId,
  isMealPhotos,
  commentingDisabled = false,
}) => {
  const [typingComment, setTypingComment] = useState("");
  const comments = isMealPhotos
    ? locals?.mealComments?.[photoId] ?? []
    : locals?.comments ?? [];
  const threadContainerRef = useRef(null);
  const lastCommentRef = useRef(null);
  const previousComments = usePrevious(comments);

  // This is to scroll to the bottom of the thread container
  // when a new comment is added --> better user experience
  useEffect(() => {
    let timeout;
    // this is to make sure we don't scroll down when deleting
    // only when adding cause then there the user would like
    // to see the new comment, otherwise it would be annoying
    const notDeletedCommentsCount = comments?.filter(
      (comment) => !comment.isDeleted
    )?.length;
    const previousNotDeletedCommentsCount = previousComments?.filter(
      (comment) => !comment.isDeleted
    )?.length;
    if (
      lastCommentRef.current &&
      comments?.length > 2 &&
      notDeletedCommentsCount > previousNotDeletedCommentsCount
    ) {
      timeout = setTimeout(() => {
        lastCommentRef?.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "end",
        });
        // also we scroll to the bottom of the body so that the
        // user could still see the comment input box
        if (bodyRef?.current) {
          bodyRef?.current?.scrollTo({
            top: bodyRef?.current?.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 500);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [comments, previousComments, bodyRef]);

  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (typingComment.length > 0 && commentActions?.add) {
      const { url, buildBody } = commentActions.add;
      submit(url, {
        method: "POST",
        body: buildBody(commentParams, {
          message: typingComment,
          senderId: locals.activeUser._id,
          receiverId: commentParams?.receiverId,
        }),
      });
      setTypingComment("");
      // Get the last child of the thread container to scroll to it in useEffect
      // this is for a better user experience
      if (threadContainerRef.current) {
        const lastChild = threadContainerRef.current.lastElementChild;
        if (lastChild) {
          lastCommentRef.current = lastChild;
        }
      }
    }
  };

  return (
    <div className="comments-section">
      <CommentsList
        commentActions={commentActions}
        commentParams={commentParams}
        lastCommentRef={lastCommentRef}
        locals={locals}
        threadContainerRef={threadContainerRef}
        photoId={photoId}
        isMealPhotos={isMealPhotos}
      />
      {!commentingDisabled && (
        <CommentInput
          isMealPhotos={isMealPhotos}
          handleSubmitComment={handleSubmitComment}
          typingComment={typingComment}
          setTypingComment={setTypingComment}
        />
      )}
    </div>
  );
};
